/**
 * @fileoverview Hover state for donut slices: the slice pops outward along
 * its mid-angle with a spring, preserving the sector's rounded-corner shape
 * (a path-morph expansion would redraw the slice with square ends).
 * @module shared/components/ui/AnimatedDonutSector
 */

import { ReactElement } from 'react'
import { motion } from 'framer-motion'
import { Sector } from 'recharts'
import type { PieSectorDataItem } from 'recharts/types/polar/Pie'

const RADIAN = Math.PI / 180
/** How far the hovered slice pops outward, in px. */
const POP_DISTANCE = 6

/**
 * `activeShape` renderer for recharts `Pie`: translates the hovered slice
 * outward along its mid-angle instead of growing its radius, so rounded
 * corners and pill caps survive the hover.
 */
export function renderActiveDonutSector(props: unknown): ReactElement {
  const p = props as PieSectorDataItem
  const midAngle = p.midAngle ?? 0
  const dx = Math.cos(-midAngle * RADIAN) * POP_DISTANCE
  const dy = Math.sin(-midAngle * RADIAN) * POP_DISTANCE

  return (
    <motion.g
      initial={{ x: 0, y: 0 }}
      animate={{ x: dx, y: dy }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <Sector
        cx={p.cx ?? 0}
        cy={p.cy ?? 0}
        innerRadius={p.innerRadius ?? 0}
        outerRadius={p.outerRadius ?? 0}
        startAngle={p.startAngle ?? 0}
        endAngle={p.endAngle ?? 0}
        fill={p.fill}
        cornerRadius={p.cornerRadius ?? 0}
        stroke="none"
      />
    </motion.g>
  )
}
